import { useReducer } from "react"

import type { PositionDef, RaceCard } from "@/types"

export const BUDGET = 1_000_000
export const APOTHECARY_COST = 50_000
export const STAFF_COST = 10_000

export interface PlayerDraft {
  id: string // client-side uuid for list keys
  position: string
  name: string
  cost: number
}

export interface RosterState {
  teamName: string
  players: PlayerDraft[]
  rerolls: number
  apothecary: boolean
  cheerleaders: number
  assistantCoaches: number
}

export type RosterAction =
  | { type: "HIRE_PLAYER"; position: PositionDef }
  | { type: "FIRE_PLAYER"; id: string }
  | { type: "RENAME_PLAYER"; id: string; name: string }
  | { type: "SET_TEAM_NAME"; name: string }
  | { type: "SET_REROLLS"; count: number }
  | { type: "TOGGLE_APOTHECARY" }
  | { type: "SET_CHEERLEADERS"; count: number }
  | { type: "SET_ASSISTANT_COACHES"; count: number }

function generatePlayerName(
  position: string,
  existingPlayers: PlayerDraft[],
): string {
  const samePosition = existingPlayers.filter((p) => p.position === position)
  return `${position} ${samePosition.length + 1}`
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function rosterReducer(
  state: RosterState,
  action: RosterAction,
): RosterState {
  switch (action.type) {
    case "HIRE_PLAYER": {
      const newPlayer: PlayerDraft = {
        id: generateId(),
        position: action.position.name,
        name: generatePlayerName(action.position.name, state.players),
        cost: action.position.cost,
      }
      return { ...state, players: [...state.players, newPlayer] }
    }
    case "FIRE_PLAYER":
      return {
        ...state,
        players: state.players.filter((p) => p.id !== action.id),
      }
    case "RENAME_PLAYER":
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === action.id ? { ...p, name: action.name } : p,
        ),
      }
    case "SET_TEAM_NAME":
      return { ...state, teamName: action.name }
    case "SET_REROLLS":
      return { ...state, rerolls: Math.max(0, action.count) }
    case "TOGGLE_APOTHECARY":
      return { ...state, apothecary: !state.apothecary }
    case "SET_CHEERLEADERS":
      return { ...state, cheerleaders: Math.max(0, action.count) }
    case "SET_ASSISTANT_COACHES":
      return { ...state, assistantCoaches: Math.max(0, action.count) }
    default:
      return state
  }
}

export function computeDerivedState(state: RosterState, race: RaceCard) {
  const playerCost = state.players.reduce((sum, p) => sum + p.cost, 0)
  const rerollCost = state.rerolls * race.reroll_cost
  const apothecaryCost = state.apothecary ? APOTHECARY_COST : 0
  const cheerleaderCost = state.cheerleaders * STAFF_COST
  const coachCost = state.assistantCoaches * STAFF_COST
  const totalCost =
    playerCost + rerollCost + apothecaryCost + cheerleaderCost + coachCost
  const remainingBudget = BUDGET - totalCost
  const overBudget = totalCost > BUDGET
  const hasEnoughPlayers = state.players.length >= 11
  const allPlayersNamed = state.players.every((p) => p.name.trim().length > 0)
  const hasTeamName = state.teamName.trim().length > 0
  const isValid =
    hasEnoughPlayers && !overBudget && allPlayersNamed && hasTeamName

  return {
    playerCost,
    rerollCost,
    apothecaryCost,
    cheerleaderCost,
    coachCost,
    totalCost,
    remainingBudget,
    overBudget,
    hasEnoughPlayers,
    allPlayersNamed,
    hasTeamName,
    isValid,
  }
}

export function positionCount(
  state: RosterState,
  positionName: string,
): number {
  return state.players.filter((p) => p.position === positionName).length
}

export function initialRosterState(): RosterState {
  return {
    teamName: "",
    players: [],
    rerolls: 0,
    apothecary: false,
    cheerleaders: 0,
    assistantCoaches: 0,
  }
}

export function useRosterReducer(race: RaceCard) {
  const [state, dispatch] = useReducer(
    rosterReducer,
    undefined,
    initialRosterState,
  )
  const derived = computeDerivedState(state, race)
  return { state, dispatch, derived }
}
